import { Award, Users, Clock, Target, CheckCircle, Briefcase } from 'lucide-react';
import { useEffect } from 'react';

const About = () => {
  useEffect(() => {
    document.title = 'Tentang Kami - CV Samudera Alam Perkasa';
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      {/* Page Header */}
      <div className="bg-navy-900 text-white py-20 mt-16">
        <div className="container-custom">
          <h1 className="text-4xl font-bold mb-4">Tentang Kami</h1>
          <p className="text-lg text-gray-300 max-w-3xl">
            Penyedia layanan tenaga kerja profesional dengan komitmen untuk membangun masa depan
            yang lebih cerah melalui solusi tenaga kerja berkualitas.
          </p>
        </div>
      </div>

      {/* Vision & Mission */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="section-title">Visi & Misi</h2>
              <div className="mb-8">
                <h3 className="text-xl font-bold mb-3 text-navy-900 flex items-center">
                  <Target size={24} className="text-primary-600 mr-2" />
                  Visi Kami
                </h3>
                <p className="text-gray-700 ml-9">
                  Menjadi penyedia layanan tenaga kerja terkemuka di Indonesia yang memberikan
                  solusi terbaik dan berkelanjutan bagi klien dan tenaga kerja.
                </p>
              </div>
              
              <div>
                <h3 className="text-xl font-bold mb-3 text-navy-900 flex items-center">
                  <CheckCircle size={24} className="text-primary-600 mr-2" />
                  Misi Kami
                </h3>
                <ul className="space-y-3 ml-9 text-gray-700">
                  <li className="flex items-start">
                    <span className="inline-block h-1.5 w-1.5 bg-primary-600 rounded-full mt-2 mr-2"></span>
                    <span>Menghubungkan talenta terbaik dengan perusahaan yang tepat</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block h-1.5 w-1.5 bg-primary-600 rounded-full mt-2 mr-2"></span>
                    <span>Memastikan pertumbuhan berkelanjutan bagi klien dan tenaga kerja</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block h-1.5 w-1.5 bg-primary-600 rounded-full mt-2 mr-2"></span>
                    <span>Mengembangkan potensi tenaga kerja Indonesia melalui pelatihan berkualitas</span>
                  </li>
                  <li className="flex items-start">
                    <span className="inline-block h-1.5 w-1.5 bg-primary-600 rounded-full mt-2 mr-2"></span>
                    <span>Menjadi mitra terpercaya dalam solusi ketenagakerjaan</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="rounded-lg overflow-hidden shadow-lg">
              <img
                src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1600"
                alt="Company vision and mission"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* History & Background */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title mx-auto inline-block after:left-1/2 after:-translate-x-1/2">
              Sejarah & Latar Belakang
            </h2>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              {/* Timeline */}
              <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-1 bg-primary-600 transform md:translate-x-0"></div>
              
              {/* Timeline Items */}
              <div className="space-y-12">
                {[
                  {
                    year: '2012',
                    title: 'Awal Mula',
                    content: 'CV Samudera Alam Perkasa didirikan di Surabaya oleh Hendro Wijaya dengan fokus awal pada penyediaan tenaga kerja untuk sektor konstruksi.',
                  },
                  {
                    year: '2015',
                    title: 'Ekspansi Layanan',
                    content: 'Perusahaan mulai memperluas jangkauan layanan ke sektor manufaktur dan industri, serta menambahkan layanan pelatihan tenaga kerja.',
                  },
                  {
                    year: '2018',
                    title: 'Pertumbuhan Signifikan',
                    content: 'Membuka kantor cabang di Jakarta dan Bandung, dengan pertumbuhan klien lebih dari 150% dalam tiga tahun.',
                  },
                  {
                    year: '2021',
                    title: 'Digitalisasi Proses',
                    content: 'Implementasi sistem manajemen digital untuk meningkatkan efisiensi dan kualitas layanan di era digital.',
                  },
                  {
                    year: '2023',
                    title: 'Pencapaian Terkini',
                    content: 'Telah melayani lebih dari 500 klien dan menyalurkan lebih dari 1500 tenaga kerja profesional ke berbagai sektor industri di Indonesia.',
                  },
                ].map((item, index) => (
                  <div key={index} className="relative flex flex-col md:flex-row items-center">
                    <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 text-right' : 'md:pl-12 md:order-1'}`}>
                      <div className={`bg-white p-6 rounded-lg shadow-md ${index % 2 === 0 ? 'ml-8 md:ml-0' : 'ml-8 md:ml-0'}`}>
                        <span className="text-primary-600 font-bold text-lg">{item.year}</span>
                        <h3 className="text-xl font-bold mt-1 mb-2 text-navy-900">{item.title}</h3>
                        <p className="text-gray-600">{item.content}</p>
                      </div>
                    </div>
                    
                    <div className="absolute left-0 md:left-1/2 flex items-center justify-center">
                      <div className="w-8 h-8 rounded-full bg-white border-4 border-primary-600 transform md:translate-x-[-50%] z-10"></div>
                    </div>
                    
                    <div className={`md:w-1/2 ${index % 2 === 0 ? 'md:order-1 md:pl-12' : 'md:pr-12 text-right'}`}></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Strengths */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title mx-auto inline-block after:left-1/2 after:-translate-x-1/2">
              Keunggulan Perusahaan
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto mt-6">
              Faktor-faktor yang membedakan kami dari kompetitor dan membuat kami menjadi pilihan tepat untuk kebutuhan tenaga kerja Anda.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Clock size={48} className="text-primary-600" />,
                title: 'Rekrutmen Cepat',
                description: 'Proses rekrutmen yang efisien dengan waktu tunggu minimal, menjamin kebutuhan tenaga kerja terpenuhi tepat waktu.',
              },
              {
                icon: <Users size={48} className="text-primary-600" />,
                title: 'Tenaga Kerja Berkualitas',
                description: 'Seleksi ketat dan pelatihan menyeluruh untuk memastikan kualitas dan profesionalisme setiap tenaga kerja.',
              },
              {
                icon: <Briefcase size={48} className="text-primary-600" />,
                title: 'Jaringan Kuat',
                description: 'Jaringan luas di seluruh Indonesia memungkinkan kami menyediakan tenaga kerja dari berbagai latar belakang dan keahlian.',
              },
              {
                icon: <Award size={48} className="text-primary-600" />,
                title: 'Tim Berpengalaman',
                description: 'Tim manajemen dengan pengalaman luas di industri ketenagakerjaan dan pemahaman mendalam tentang kebutuhan klien.',
              },
              {
                icon: <Target size={48} className="text-primary-600" />,
                title: 'Solusi Komprehensif',
                description: 'Layanan lengkap dari rekrutmen hingga pengelolaan dan pengembangan tenaga kerja dalam satu atap.',
              },
              {
                icon: <CheckCircle size={48} className="text-primary-600" />,
                title: 'Kepatuhan Regulasi',
                description: 'Memastikan semua proses ketenagakerjaan sesuai dengan regulasi pemerintah dan standar industri terkini.',
              },
            ].map((strength, index) => (
              <div key={index} className="card p-6 hover:translate-y-[-8px] transition-all">
                <div className="mb-4">{strength.icon}</div>
                <h3 className="text-xl font-bold mb-3 text-navy-900">{strength.title}</h3>
                <p className="text-gray-600">{strength.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default About;